import React from "react";
import _ from "lodash";
import { compose } from "recompose";
import { connect } from "react-redux";
import { FormattedMessage } from "react-intl";

import { connectToApi, ActionCreator } from "@api/components/connectToApi";
import { Api } from "@api/index";
import { AuthState } from "@auth/models";
import { FilesState } from "@files/models";
import { Album, Track, MusicState } from "@music/models";
import { messages as mMessages } from "@music/messages";
import { Album as AlbumEl } from "@music/components/Album";
import { TrackListTable } from "@music/components/TrackListTable";
import { messages as uiMessages } from "@ui/messages";
import { Spinner } from "@ui/Spinner";
import { SearchState } from "../../models";
import {
    getSearchLastLoaded,
    getSearchResponseQuery,
    getSearchIsLoading,
    getSearchResults,
} from "../../selectors";
import { messages } from "../../messages";

type State = SearchState & MusicState & FilesState & AuthState;

interface SearchResultsOwnProps {
    query: string;
}

export interface SearchResultsProps extends SearchResultsOwnProps {
    albums: Album[];
    tracks: Track[];
    responseQuery: string;
    loading: boolean;
}

const SearchResults = ({ query, responseQuery, loading, albums, tracks }: SearchResultsProps) => {
    const albumResults = _.map(
        albums,
        (album) => <li key={album.id}><AlbumEl album={album} /></li>,
    );
    let message = messages.searchResultsFound;
    if (loading && responseQuery == null) {
        message = uiMessages.loading;
    }

    let messageEls: JSX.Element[] = [
        <h2 key="title" className="display-1">
            <FormattedMessage {...messages.searchResultsError} />
        </h2>,
    ];
    if (query !== "") {
        messageEls = [
            <h2 key="title" className="display-1">
                <FormattedMessage {...messages.searchResultsTitle}
                    values={{query: responseQuery ? responseQuery : query}} />
            </h2>,
            <p key="paragraph">
                <FormattedMessage {...message} values={{
                    albums: albums.length,
                    tracks: tracks.length,
                    total: tracks.length + albums.length,
                }} />
            </p>,
        ];
    }
    return (
        <div className="search-results">
            {loading && <Spinner key="spinner" />}
            {messageEls}
            {albums.length > 0 &&
                <div key="albums" className="mb-3">
                    <h3 className="display-2"><FormattedMessage {...mMessages.albumEntityPlural} /></h3>
                    <ol key="results">
                        {albumResults}
                    </ol>
                </div>
            }
            {tracks.length > 0 &&
                <div key="tracks">
                    <h3 className="display-2"><FormattedMessage {...mMessages.trackEntityPlural} /></h3>
                    <TrackListTable tracks={tracks} />
                </div>
            }
        </div>
    );
};

const mapStateToProps = (state: State, props: SearchResultsOwnProps) => {
    const results = getSearchResults(state);
    let actionCreators: ActionCreator[] = [];
    let lastLoaded: Date[] = [];
    let requestId: string[] = [];
    if (props.query !== "") {
        actionCreators = [ () => Api.search(props.query) ];
        lastLoaded = [ getSearchLastLoaded(state) ];
        requestId = [ props.query ];
    }
    return {
        actionCreators,
        lastLoaded,
        requestId,
        albums: results.albums,
        tracks: results.tracks,
        responseQuery: getSearchResponseQuery(state),
        loading: getSearchIsLoading(state),
    };
};

const ConnectedSearchResults = compose<SearchResultsProps, SearchResultsOwnProps>(
    connect(mapStateToProps, null),
    connectToApi({
        showRefresh: false,
        reloadOnMount: true,
    }),
)(SearchResults);

export {
    SearchResults as SearchResultsPresenter,
    ConnectedSearchResults as SearchResults,
};
